import { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useParams } from 'react-router-dom';
import { LogOut, HelpCircle, MapPin, Phone, Mail, Globe } from 'lucide-react';
import { useClientAuth } from '../auth/ClientAuthContext';
import API_BASE from '../utils/api';
import '../styles/ClientPortal.css';

function formatProjectAddress(address) {
  if (!address) return '';
  const clean = (s) => (typeof s === 'string' ? s.trim() : '');
  const line1 = [clean(address.street), clean(address.street2)].filter(Boolean).join(' ');
  const line2 = [clean(address.city), [clean(address.state), clean(address.zip)].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [line1, line2].filter(Boolean).join(', ');
}

export default function ClientPortalLayout() {
  const { clientAuth, clientLogout } = useClientAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [project, setProject] = useState(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  useEffect(() => {
    if (!clientAuth?.token) return;
    fetch(`${API_BASE}/api/client/designer-info`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${clientAuth.token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setCompany)
      .catch(() => setCompany(null));
  }, [clientAuth]);

  useEffect(() => {
    if (!clientAuth?.token || !projectId) {
      setProject(null);
      return;
    }
    fetch(`${API_BASE}/api/client/projects/${projectId}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${clientAuth.token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setProject)
      .catch(() => setProject(null));
  }, [clientAuth, projectId]);

  async function handleLogout() {
    await clientLogout();
    navigate('/client/login', { replace: true });
  }

  const addressLine = formatProjectAddress(project?.address);
  const hasCompanyInfo = company && (company.companyName || company.logoUrl);

  return (
    <div className="cp-shell">
      <aside className="cp-sidebar">
        <Link to="/client/dashboard" className="cp-sidebar-brand">
          <img src="/logo.png" alt="InteriorFolio" className="cp-sidebar-brand-logo" />
        </Link>

        {project && (
          <div className="cp-sidebar-welcome">
            <p className="cp-sidebar-eyebrow">Welcome,</p>
            <p className="cp-sidebar-project-name">{project.projectNickname || 'Untitled Project'}</p>
            {addressLine && (
              <p className="cp-sidebar-address">
                <MapPin size={15} className="cp-sidebar-icon" />
                {addressLine}
              </p>
            )}
          </div>
        )}

        <div className="cp-sidebar-spacer" />

        {hasCompanyInfo && (
          <div className="cp-sidebar-company">
            <div className="cp-sidebar-divider" />
            {company.logoUrl ? (
              <div className="cp-sidebar-company-logo">
                <img src={company.logoUrl} alt={company.companyName || 'Company logo'} />
              </div>
            ) : null}
            {company.companyName && <p className="cp-sidebar-company-name">{company.companyName}</p>}
            {company.address && <p className="cp-sidebar-company-line">{company.address}</p>}
            {company.contactNumber && (
              <p className="cp-sidebar-company-row">
                <Phone size={17} className="cp-sidebar-icon" />
                <span>
                  <span className="cp-sidebar-company-label">Phone:</span>
                  <br />
                  {company.contactNumber}
                </span>
              </p>
            )}
            {company.email && (
              <p className="cp-sidebar-company-row">
                <Mail size={17} className="cp-sidebar-icon" />
                <span>
                  <span className="cp-sidebar-company-label">Email:</span>
                  <br />
                  {company.email}
                </span>
              </p>
            )}
            {company.website && (
              <p className="cp-sidebar-company-row">
                <Globe size={17} className="cp-sidebar-icon" />
                <span>
                  <span className="cp-sidebar-company-label">Website:</span>
                  <br />
                  {company.website}
                </span>
              </p>
            )}
          </div>
        )}
      </aside>

      <div className="cp-content-col">
        <div className="cp-topbar">
          <button
            className="cp-account-btn"
            onClick={() => setShowAccountMenu((v) => !v)}
            aria-label="Account menu"
          >
            {(clientAuth?.name || clientAuth?.email || '?').charAt(0).toUpperCase()}
          </button>
          {showAccountMenu && (
            <div className="cp-account-menu" onMouseLeave={() => setShowAccountMenu(false)}>
              <p className="cp-account-menu-name">{clientAuth?.name || clientAuth?.email}</p>
              <Link
                to="/client/faq"
                className="cp-account-menu-item"
                onClick={() => setShowAccountMenu(false)}
              >
                <HelpCircle size={13} />
                Help &amp; FAQ
              </Link>
              <button className="cp-account-menu-item" onClick={handleLogout}>
                <LogOut size={13} />
                Sign out
              </button>
            </div>
          )}
        </div>

        <main className="cp-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
